import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

export default function PaymentSchedulePage({ cabinetId = null }) {
  const [projects, setProjects] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    if (cabinetId == null) return;
    loadAll();
  }, [cabinetId]);

  async function loadAll() {
    // 1. Charger les projets (projects a cabinet_id)
    const { data: projectsData, error: projError } = await supabase
      .from("projects")
      .select("*")
      .eq("cabinet_id", cabinetId);

    if (projError) {
      console.error("Erreur chargement projets:", projError);
      return;
    }
    const projectList = projectsData || [];
    setProjects(projectList);
    const ids = projectList.map((p) => p.id);

    // 2. payment_schedule et finance n'ont PAS cabinet_id — filtrer par project_id
    const [scheduleRes, financeRes] = await Promise.all([
      ids.length > 0
        ? supabase.from("payment_schedule").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
      ids.length > 0
        ? supabase.from("finance").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
    ]);

    if (scheduleRes.error) console.error("Erreur chargement échéancier:", scheduleRes.error);
    else setSchedule(scheduleRes.data || []);

    if (financeRes.error) console.error("Erreur chargement finance:", financeRes.error);
    else setFinanceEntries(financeRes.data || []);
  }

  function getProjectSchedule(projectId) {
    return schedule
      .filter((s) => String(s.project_id) === String(projectId))
      .sort((a, b) => a.installment_number - b.installment_number);
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  function getExpectedAmount(projectBudget, percentage) {
    const budget = Number(projectBudget || 0);
    const pct = Number(percentage || 0);
    return (budget * pct) / 100;
  }

  function getEncaisseForInstallment(projectId, installmentNumber) {
    return financeEntries
      .filter(
        (entry) =>
          String(entry.project_id) === String(projectId) &&
          entry.type === "income" &&
          Number(entry.installment_number) === Number(installmentNumber)
      )
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getStatutEcheance(attendu, encaisse) {
    if (encaisse >= attendu && attendu > 0) return "Conforme";
    if (encaisse > 0 && encaisse < attendu) return "Partiel";
    return "Impayé";
  }

  function getTimelineStatut(row, attendu, encaisse) {
    const rawStatus = String(row?.status || "").toLowerCase();
    if (rawStatus.includes("pending") || rawStatus.includes("attente")) return "En attente";
    const statut = getStatutEcheance(attendu, encaisse);
    if (statut === "Conforme") return "Payé";
    if (statut === "Partiel") return "Partiellement payé";
    return "Impayé";
  }

  function getStatutBadgeStyle(statut) {
    if (statut === "Payé") {
      return {
        background: "rgba(92,138,92,0.14)",
        color: "#5C8A5C",
        border: "1px solid rgba(92,138,92,0.4)",
      };
    }
    if (statut === "En attente") {
      return {
        background: "rgba(192,154,91,0.14)",
        color: "#C09A5B",
        border: "1px solid rgba(192,154,91,0.4)",
      };
    }
    if (statut === "Partiellement payé") {
      return {
        background: "rgba(74,122,155,0.14)",
        color: "#4A7A9B",
        border: "1px solid rgba(74,122,155,0.4)",
      };
    }
    return {
      background: "rgba(184,92,58,0.14)",
      color: "#B85C3A",
      border: "1px solid rgba(184,92,58,0.4)",
    };
  }

  function getProjectQuickStatus(project) {
    const rows = getProjectSchedule(project.id);
    if (rows.length === 0) return "En attente";
    const hasImpayes = rows.some((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const encaisse = getEncaisseForInstallment(project.id, row.installment_number);
      return getTimelineStatut(row, attendu, encaisse) === "Impayé";
    });
    if (hasImpayes) return "Impayé";
    const hasPartials = rows.some((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const encaisse = getEncaisseForInstallment(project.id, row.installment_number);
      return getTimelineStatut(row, attendu, encaisse) === "Partiellement payé";
    });
    if (hasPartials) return "Partiellement payé";
    return "Payé";
  }

  const selectedProject = projects.find(
    (p) => String(p.id) === String(selectedProjectId)
  );

  const selectedProjectSchedule = selectedProjectId
    ? getProjectSchedule(selectedProjectId)
    : [];

  const totalPercentage = selectedProjectSchedule.reduce(
    (sum, row) => sum + Number(row.percentage || 0),
    0
  );

  const totalExpected = selectedProjectSchedule.reduce(
    (sum, row) => sum + getExpectedAmount(selectedProject?.budget, row.percentage),
    0
  );

  const totalEncaisse = selectedProjectSchedule.reduce(
    (sum, row) =>
      sum +
      getEncaisseForInstallment(selectedProjectId, row.installment_number),
    0
  );

  const totalRestant = Math.max(totalExpected - totalEncaisse, 0);

  const timelineProgress = totalExpected > 0 ? Math.min(100, Math.round((totalEncaisse / totalExpected) * 100)) : 0;
  const fallbackProjects = [...projects].slice(0, 3);

  return (
    <main style={{ minHeight: "100vh", background: "#F8F4EE", padding: 24 }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style><header style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18, marginBottom: 18 }}><div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}><div><h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 34, fontWeight: 700 }}>
              Échéancier de paiement
            </h1><p style={{ margin: "6px 0 0", fontFamily: "'DM Sans', sans-serif", color: "#8A7664", fontSize: 14 }}>
              Suivi vertical des jalons financiers par projet.
            </p></div><div style={{ minWidth: 300, width: 360 }}><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Projet
            </label><select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              style={{
                width: "100%",
                border: "1px solid #D8C9B8",
                borderRadius: 8,
                padding: "10px 12px",
                background: "#FFFFFF",
                fontFamily: "'DM Sans', sans-serif",
                color: "#3D2E20",
                fontSize: 14,
              }}
            ><option value="">Sélectionner un projet</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select></div></div>

        {selectedProject && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginTop: 16 }}><div style={{ background: "#F8F4EE", borderRadius: 10, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Budget total</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#3D2E20" }}>{formatMoney(selectedProject.budget)}</div></div><div style={{ background: "#F8F4EE", borderRadius: 10, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Total échéancier</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#3D2E20" }}>{formatMoney(totalExpected)}</div></div><div style={{ background: "#F8F4EE", borderRadius: 10, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Solde restant</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: totalRestant > 0 ? "#B85C3A" : "#5C8A5C" }}>{formatMoney(totalRestant)}</div></div></div>
        )}
      </header>

      {selectedProject ? (
        <section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#3D2E20", marginBottom: 12 }}>
            Timeline des échéances — {selectedProject.name}
          </div><div style={{ display: "grid", gap: 12 }}>
            {selectedProjectSchedule.map((row) => {
              const attendu = getExpectedAmount(selectedProject.budget, row.percentage);
              const encaisse = getEncaisseForInstallment(selectedProject.id, row.installment_number);
              const statut = getTimelineStatut(row, attendu, encaisse);
              return (
                <article key={row.id} style={{ border: "1px solid #E9DDCF", borderRadius: 10, padding: 14, background: "#FDFBF8" }}><div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}><div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664", marginBottom: 6 }}>
                        V{row.installment_number} — {row.label || `Jalon ${row.milestone_number}`}
                      </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, lineHeight: 1.1, fontWeight: 700, color: "#3D2E20" }}>
                        {formatMoney(attendu)}
                      </div><div style={{ marginTop: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>
                        Date prévue : {row.due_date || row.expected_date || "Non définie"}
                      </div></div><div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}><span
                        style={{
                          display: "inline-block",
                          borderRadius: 999,
                          padding: "5px 10px",
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          ...getStatutBadgeStyle(statut),
                        }}
                      >
                        {statut}
                      </span><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664", textAlign: "right" }}>
                        Encaissé : <strong style={{ color: "#3D2E20" }}>{formatMoney(encaisse)}</strong></div></div></div></article>
              );
            })}
          </div><div style={{ marginTop: 16, borderTop: "1px solid #E9DDCF", paddingTop: 14 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}><span>Progression globale</span><strong style={{ color: "#3D2E20" }}>{timelineProgress}%</strong></div><div style={{ width: "100%", height: 10, background: "#EFE4D4", borderRadius: 999 }}><div style={{ width: `${timelineProgress}%`, height: "100%", borderRadius: 999, background: "#C09A5B" }} /></div></div></section>
      ) : (
        <section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 18 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#3D2E20", marginBottom: 12 }}>
            Projets récents
          </div><div style={{ display: "grid", gap: 10 }}>
            {fallbackProjects.map((project) => {
              const projectSchedule = getProjectSchedule(project.id);
              const expected = projectSchedule.reduce((sum, row) => sum + getExpectedAmount(project.budget, row.percentage), 0);
              const encaisse = projectSchedule.reduce((sum, row) => sum + getEncaisseForInstallment(project.id, row.installment_number), 0);
              const quickStatus = getProjectQuickStatus(project);
              return (
                <div key={project.id} style={{ border: "1px solid #E9DDCF", borderRadius: 10, padding: 12, background: "#FDFBF8", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}><div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#3D2E20", fontWeight: 700 }}>{project.name}</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664", marginTop: 4 }}>
                      Budget: {formatMoney(project.budget)} · Échéancier: {formatMoney(expected)}
                    </div></div><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>
                      Encaissé: <strong style={{ color: "#3D2E20" }}>{formatMoney(encaisse)}</strong></div><span style={{ borderRadius: 999, padding: "5px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", ...getStatutBadgeStyle(quickStatus) }}>
                      {quickStatus}
                    </span></div></div>
              );
            })}
            {fallbackProjects.length === 0 && (
              <div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8A7664" }}>
                Aucun projet disponible pour le moment.
              </div>
            )}
          </div></section>
      )}
    </main>
  );
}